/**
 * Client-side localStorage utilities for sandbox (non-registered) onboarding.
 * Used to persist interests, progress, and "seen" state so we don't ask again
 * on repeat visits. When the user registers, TourProvider migrates these to the API.
 */

const STORAGE_KEYS = {
  interests: "qw_sandbox_interests",
  progress: "qw_sandbox_progress",
  onboardingSeen: "qw_sandbox_onboarding_seen",
} as const;

function safeParse<T>(raw: string | null, fallback: T): T {
  if (raw == null) return fallback;
  try {
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

export function getSandboxInterests(): string[] {
  if (typeof window === "undefined") return [];
  const raw = localStorage.getItem(STORAGE_KEYS.interests);
  const arr = safeParse<string[]>(raw, []);
  return Array.isArray(arr) ? arr : [];
}

export function setSandboxInterests(interests: string[]): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.interests, JSON.stringify(interests));
}

export function getSandboxProgress(): Record<string, boolean> {
  if (typeof window === "undefined") return {};
  const raw = localStorage.getItem(STORAGE_KEYS.progress);
  const obj = safeParse<Record<string, boolean>>(raw, {});
  return obj && typeof obj === "object" ? obj : {};
}

export function setSandboxModuleCompleted(moduleId: string): void {
  if (typeof window === "undefined") return;
  const current = getSandboxProgress();
  if (current[moduleId]) return;
  localStorage.setItem(
    STORAGE_KEYS.progress,
    JSON.stringify({ ...current, [moduleId]: true })
  );
}

export function hasSandboxOnboardingSeen(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEYS.onboardingSeen) === "1";
}

export function setSandboxOnboardingSeen(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.onboardingSeen, "1");
}

/**
 * Clear all sandbox onboarding data (e.g. after successful migration to API).
 */
export function clearSandboxOnboarding(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(STORAGE_KEYS.interests);
  localStorage.removeItem(STORAGE_KEYS.progress);
  localStorage.removeItem(STORAGE_KEYS.onboardingSeen);
}

/* ─── Pre-registration usage dedup helpers ─── */

function todayKey(feature: string): string {
  const d = new Date().toISOString().slice(0, 10);
  return `qw_pre_tracked_${feature}_${d}`;
}

export function hasTrackedToday(feature: string): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(todayKey(feature)) === "1";
}

export function markTrackedToday(feature: string): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(todayKey(feature), "1");
}

/**
 * Fire-and-forget pre-registration usage tracker.
 * Sends to /api/track/pre-reg which upserts a PreRegistrationUsage row.
 * Uses localStorage dedup so we only call the API once per feature per day.
 */
export function trackPreRegUsage(
  feature: string,
  metadata?: Record<string, unknown>,
  opts?: { fingerprintId?: string; skipDedup?: boolean },
): void {
  if (typeof window === "undefined") return;
  if (!opts?.skipDedup && hasTrackedToday(feature)) return;
  markTrackedToday(feature);

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts?.fingerprintId) {
    headers["x-visitor-id"] = opts.fingerprintId;
  }

  fetch("/api/track/pre-reg", {
    method: "POST",
    headers,
    body: JSON.stringify({ feature, metadata }),
    keepalive: true,
  }).catch(() => {});
}

export { STORAGE_KEYS };
